import type {
  ProjectConfig,
  HeaderSetting,
  MenuSetting,
  TransitionSetting,
  MultiTabsSetting,
} from '/#/config';
import type { BeforeMiniState, SelectedApp } from '/#/store';

import { defineStore } from 'pinia';
import { store } from '/@/store';

import { ThemeEnum } from '/@/enums/appEnum';
import { APP_DARK_MODE_KEY_ } from '/@/enums/cacheEnum';
import { Persistent } from '/@/utils/cache/persistent';
import { darkMode, prefixCls, themeColor } from '/@/settings/designSetting';
import { resetRouter } from '/@/router';
import { deepMerge } from '/@/utils';
import projectSetting from '/@/settings/projectSetting';
import defaultLogo from '../../assets/images/logo1.svg';

interface AppState {
  darkMode?: ThemeEnum;
  themeColor: string;
  // Page loading status
  pageLoading: boolean;
  // project config
  projectConfig: ProjectConfig | null;
  // When the window shrinks, remember some states, and restore these states when the window is restored
  beforeMiniInfo: BeforeMiniState;

  selectedApp: SelectedApp;

  showAppFlag: Boolean;

  flag: Boolean;

  logo: string;
  prefixCls: string;

  /**
   * 2d或3d类型
   */
  workBranchType: string;
}
let timeId: TimeoutHandle;

function switchSubDomain(to) {
  const { hostname } = window.location;

  // 本地环境直接返回，不切换子域名
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
    return;
  }

  const parts = hostname.split('.');
  parts[0] = to;

  window.location.hostname = parts.join('.');
}

export const useAppStore = defineStore({
  id: 'app',
  state: (): AppState => ({
    darkMode: darkMode,
    prefixCls: prefixCls,
    themeColor: themeColor,
    pageLoading: false,
    projectConfig: projectSetting,
    beforeMiniInfo: {},
    // 这里临时先写死 序达
    selectedApp: {
      tenantId: '',
      appCode: '',
    },
    showAppFlag: false,
    flag: false,
    logo: defaultLogo,
    workBranchType: window.location.hostname.split('.')[0] === '3d' ? '3d' : '2d',
  }),
  getters: {
    getPageLoading(): boolean {
      return this.pageLoading;
    },
    getDarkMode(): 'light' | 'dark' | string {
      return this.darkMode || darkMode;
    },
    getPrefixCls(): string {
      return this.prefixCls || prefixCls;
    },
    getThemeColor(): string {
      return this.themeColor;
    },

    getBeforeMiniInfo(): BeforeMiniState {
      return this.beforeMiniInfo;
    },

    getProjectConfig(): ProjectConfig {
      return this.projectConfig || ({} as ProjectConfig);
    },

    getHeaderSetting(): HeaderSetting {
      return this.getProjectConfig.headerSetting;
    },
    getMenuSetting(): MenuSetting {
      return this.getProjectConfig.menuSetting;
    },
    getTransitionSetting(): TransitionSetting {
      return this.getProjectConfig.transitionSetting;
    },
    getMultiTabsSetting(): MultiTabsSetting {
      return this.getProjectConfig.multiTabsSetting;
    },
    getSelectedApp(): SelectedApp {
      return this.selectedApp;
    },
    getShowAppFlag(): Boolean {
      return this.showAppFlag;
    },
    getLogo(): string {
      return this.logo ?? defaultLogo;
    },
    getWorkBranchType(): string {
      return this.workBranchType;
    },
  },
  actions: {
    setFlag(flag) {
      this.flag = flag;
    },
    setPageLoading(loading: boolean): void {
      this.pageLoading = loading;
    },
    setWorkBranchType(type: string): void {
      this.workBranchType = type;
      if (type === '2d') {
        switchSubDomain('v3');
      } else {
        switchSubDomain('3d');
      }
    },
    setDarkMode(mode: ThemeEnum): void {
      const htmlRoot = document.getElementById('htmlRoot');
      const pathWithTheme =
        (window.location.pathname.includes('shanshe2') ||
          window.location.pathname.includes('shanshe-enterprise')) &&
        !window.location.pathname.includes('shanshe-enterprise/data_annotation') &&
        !window.location.pathname.includes('shanshe-enterprise/shanshe-client') &&
        !window.location.pathname.includes('shanshe-enterprise/data');
      if (htmlRoot && mode && pathWithTheme) {
        this.darkMode = mode;
        htmlRoot.setAttribute('data-theme', mode);
        localStorage.setItem(APP_DARK_MODE_KEY_, mode);
      } else {
        this.darkMode = darkMode;
        htmlRoot?.setAttribute('data-theme', darkMode);
      }
    },

    setPrefixCls(prefixCls: string): void {
      this.prefixCls = prefixCls;
      const unThemePathArr = [
        'shanshe-enterprise/data',
        'shanshe-client/index',
        'shanshe-enterprise/privilege-manage',
        // 'do-back'
      ];
      const pathname = window.location.pathname;
      if (unThemePathArr.some((key) => pathname.includes(key))) {
        this.prefixCls = 'ant';
      }
    },
    setThemeColor(color: string): void {
      this.themeColor = color;
      document.documentElement.style.setProperty('--primary-color', color); // 动态修改颜色
      // localStorage.setItem(APP_DARK_MODE_KEY_, mode);
    },

    setBeforeMiniInfo(state: BeforeMiniState): void {
      this.beforeMiniInfo = state;
    },

    setProjectConfig(config: DeepPartial<ProjectConfig>): void {
      // if (config.menuSetting?.show) console.trace(this.projectConfig?.menuSetting.show);
      this.projectConfig = deepMerge(this.projectConfig || {}, config);
      // Persistent.setLocal(PROJ_CFG_KEY, this.projectConfig);
    },

    setSelectedApp(tenantId: string, appCode: string) {
      this.selectedApp = {
        tenantId: tenantId,
        appCode: appCode,
      };
    },

    setShowAppFlag(flag: boolean) {
      this.showAppFlag = flag;
    },

    async resetAllState() {
      resetRouter();
      Persistent.clearAll();
    },
    async setPageLoadingAction(loading: boolean): Promise<void> {
      if (loading) {
        clearTimeout(timeId);
        // Prevent flicker
        timeId = setTimeout(() => {
          this.setPageLoading(loading);
        }, 50);
      } else {
        this.setPageLoading(loading);
        clearTimeout(timeId);
      }
    },
    setLogo(logo?: string): void {
      this.logo = logo ?? defaultLogo;
    },
  },
});

// Need to be used outside the setup
export function useAppStoreWithOut() {
  return useAppStore(store);
}
